import { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Calendar, CheckCircle2, X } from 'lucide-react';
import { enquiriesApi, enquiryHelpOptions, enquiryTimeframes } from './enquiriesApi';

export default function AdviserConversationDialog({
  slug,
  firmName,
  preview = false,
}: {
  slug?: string;
  firmName: string;
  preview?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef<string | null>(null);
  const submitting = useRef(false);
  const disabled = preview || !slug;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (pending) return;
        if (next) {
          requestId.current = null;
          setSent(false);
          setError('');
        }
        setOpen(next);
      }}
    >
      <Dialog.Trigger asChild>
        <button type="button" className="adviser-conversation-trigger" disabled={disabled}>
          <Calendar size={17} aria-hidden="true" /> Book a conversation
        </button>
      </Dialog.Trigger>
      {preview && (
        <span className="adviser-muted">Conversation requests are disabled in preview.</span>
      )}
      <Dialog.Portal>
        <Dialog.Overlay className="adviser-conversation-overlay" />
        <Dialog.Content
          className="adviser-conversation-dialog"
          onEscapeKeyDown={(event) => {
            if (pending) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (pending) event.preventDefault();
          }}
        >
          <Dialog.Close asChild>
            <button
              type="button"
              className="adviser-conversation-close"
              aria-label="Close conversation request"
              disabled={pending}
            >
              <X size={20} />
            </button>
          </Dialog.Close>
          <p className="adviser-eyebrow">Connect through StratPlan</p>
          <Dialog.Title>
            {sent ? 'Your request has been received' : `A conversation with ${firmName}`}
          </Dialog.Title>
          <Dialog.Description>
            {sent
              ? `${firmName} can now review your request and contact you to arrange a conversation. A confirmation email is on its way to you.`
              : `Tell ${firmName} a little about your business so they can respond with useful next steps. They will contact you to arrange a suitable time.`}
          </Dialog.Description>
          {sent ? (
            <div className="adviser-conversation-success" role="status">
              <CheckCircle2 size={34} aria-hidden="true" />
              <p>This is a conversation request. A meeting time has not yet been booked.</p>
              <Dialog.Close asChild>
                <button type="button" className="adviser-button">
                  Done
                </button>
              </Dialog.Close>
            </div>
          ) : (
            <form
              onSubmit={async (event) => {
                event.preventDefault();
                if (submitting.current || disabled) return;
                const form = event.currentTarget;
                const values = new FormData(form);
                const value = (key: string) => String(values.get(key) || '').trim();
                if (!form.checkValidity() || values.get('consent') !== 'on') {
                  form.reportValidity();
                  return;
                }
                if (
                  value('name').length < 2 ||
                  value('company').length < 2 ||
                  value('message').length < 10
                ) {
                  setError(
                    'Enter your name and business, and a little more about what you would like to achieve (at least 10 characters).'
                  );
                  return;
                }
                submitting.current = true;
                setPending(true);
                setError('');
                requestId.current ||= crypto.randomUUID();
                try {
                  await enquiriesApi.submit(slug!, {
                    requestId: requestId.current,
                    name: value('name'),
                    email: value('email'),
                    company: value('company'),
                    ...(value('website') && { website: value('website') }),
                    ...(value('phone') && { phone: value('phone') }),
                    helpWith: value('helpWith'),
                    message: value('message'),
                    timeframe: value('timeframe'),
                    consent: true,
                    websiteTrap: value('websiteTrap'),
                  });
                  setSent(true);
                } catch (err) {
                  setError(
                    err instanceof Error
                      ? err.message
                      : 'Your request could not be sent. Please try again.'
                  );
                } finally {
                  submitting.current = false;
                  setPending(false);
                }
              }}
            >
              <fieldset disabled={pending} className="adviser-conversation-fields">
                <div className="adviser-form-grid">
                  <label>
                    Your name
                    <input name="name" autoComplete="name" required minLength={2} maxLength={120} />
                  </label>
                  <label>
                    Email address
                    <input
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      maxLength={254}
                    />
                  </label>
                  <label className="adviser-form-full">
                    Business name
                    <input
                      name="company"
                      autoComplete="organization"
                      required
                      minLength={2}
                      maxLength={200}
                    />
                  </label>
                  <label>
                    Website <span>(optional)</span>
                    <input
                      name="website"
                      type="url"
                      autoComplete="url"
                      maxLength={2048}
                      placeholder="https://…"
                    />
                  </label>
                  <label>
                    Phone <span>(optional)</span>
                    <input name="phone" type="tel" autoComplete="tel" maxLength={80} />
                  </label>
                  <label className="adviser-form-full">
                    What would you like help with?
                    <select name="helpWith" required defaultValue="">
                      <option value="" disabled>
                        Choose a topic
                      </option>
                      {enquiryHelpOptions.map((option) => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="adviser-form-full">
                    What would you like to achieve?
                    <textarea
                      name="message"
                      required
                      minLength={10}
                      maxLength={3000}
                      rows={3}
                      placeholder="A sentence or two about your goal or current challenge."
                    />
                  </label>
                  <label className="adviser-form-full">
                    When are you looking to get started?
                    <select name="timeframe" required defaultValue="">
                      <option value="" disabled>
                        Choose a timeframe
                      </option>
                      {enquiryTimeframes.map((option) => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="adviser-enquiry-trap" aria-hidden="true">
                  <label>
                    Leave this field empty
                    <input name="websiteTrap" tabIndex={-1} autoComplete="off" />
                  </label>
                </div>
                <label className="adviser-conversation-consent">
                  <input name="consent" type="checkbox" required />{' '}
                  <span>
                    I agree to share these details with {firmName} through StratPlan so they can
                    respond to my enquiry.
                  </span>
                </label>
              </fieldset>
              {error && (
                <p className="adviser-error" role="alert">
                  {error}
                </p>
              )}
              <div className="adviser-conversation-footer">
                <p>No meeting time is booked until you agree one with the adviser.</p>
                <button type="submit" className="adviser-button" disabled={pending}>
                  {pending ? 'Sending request…' : 'Send conversation request'}
                </button>
              </div>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
